import React, { useState, useRef } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Upload, Sparkles, X, FileText, Loader } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import CFLogo from "@/components/CFLogo";
import AIAnalysisCard from "@/components/AIAnalysisCard";

const QuickAnalyze = () => {
  const { user, api } = useAuth();
  const [files, setFiles] = useState([]);
  const [businessName, setBusinessName] = useState("");
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  if (user === null) return <div className="min-h-screen flex items-center justify-center bg-cf-black text-cf-muted">Loading…</div>;
  if (user === false) return <Navigate to="/agent/login" replace />;
  if (user.role !== "agent") return <Navigate to="/merchant/dashboard" replace />;

  const addFiles = (incoming) => {
    const all = [...files, ...Array.from(incoming)].slice(0, 10);
    setFiles(all);
  };
  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const analyze = async () => {
    if (files.length === 0) return;
    setAnalyzing(true);
    setResult(null);
    const fd = new FormData();
    files.forEach((f) => fd.append("files", f));
    if (businessName.trim()) fd.append("business_name", businessName.trim());
    try {
      const { data } = await axios.post(`${api.defaults.baseURL}/analyze/standalone`, fd, {
        withCredentials: true,
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 180000,
      });
      setResult(data);
    } catch (err) {
      setResult({ error: err?.response?.data?.detail || err.message || "Analysis failed" });
    } finally {
      setAnalyzing(false);
    }
  };

  const reset = () => {
    setFiles([]);
    setBusinessName("");
    setResult(null);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#05070A", color: "#F8F9FA" }} data-testid="quick-analyze-page">
      <header className="border-b border-white/5" style={{ background: "rgba(10,14,23,0.4)" }}>
        <div className="max-w-5xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-5">
            <CFLogo variant="paper" testId="quick-analyze-logo" />
            <div className="hidden md:flex items-center gap-2 pl-5 border-l border-white/10">
              <span className="text-[0.7rem] uppercase tracking-[0.24em] text-cf-gold font-semibold">Quick Analyzer</span>
            </div>
          </div>
          <Link to="/agent/dashboard" className="text-xs text-cf-muted hover:text-cf-gold-light cf-link-underline flex items-center gap-1">
            <ArrowLeft size={12} /> Back to dashboard
          </Link>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 md:px-10 py-10">
        <h1 className="font-serif text-3xl md:text-4xl tracking-tight mb-2">
          Drop bank statements. Get an instant underwriting memo.
        </h1>
        <p className="text-cf-muted text-sm max-w-2xl">
          Upload one or more PDFs (up to 10 files, 10 MB each). The AI extracts deposits,
          balance trends, NSF count, and produces a recommendation in seconds. No lead
          needed — perfect for screening files before they hit your inbox.
        </p>

        <div className="grid lg:grid-cols-12 gap-6 mt-8">
          <div className="lg:col-span-7">
            <div className="border border-white/5 rounded-lg p-6" style={{ background: "rgba(10,14,23,0.4)" }}>
              <div className="mb-4">
                <label className="text-[0.65rem] uppercase tracking-[0.2em] text-cf-muted">Business name (optional)</label>
                <input
                  data-testid="qa-business-name"
                  value={businessName}
                  onChange={(e) => setBusinessName(e.target.value)}
                  placeholder="e.g. Acme Logistics LLC"
                  className="cf-input mt-2"
                />
              </div>

              <div
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragOver(false);
                  if (e.dataTransfer.files) addFiles(e.dataTransfer.files);
                }}
                onClick={() => inputRef.current?.click()}
                className="cursor-pointer rounded-lg p-10 text-center transition border"
                style={{
                  borderStyle: "dashed",
                  borderColor: dragOver ? "#B68A35" : "rgba(255,255,255,0.12)",
                  background: dragOver ? "rgba(182,138,53,0.06)" : "transparent",
                }}
              >
                <Upload size={28} className="mx-auto text-cf-gold-light mb-3" strokeWidth={1.5} />
                <div className="text-cf-text text-sm font-semibold">Drop PDFs here or click to browse</div>
                <div className="text-cf-muted text-xs mt-1">Bank statements, tax returns, AR aging — anything in PDF form.</div>
                <input
                  ref={inputRef} type="file" hidden multiple accept="application/pdf,image/*"
                  onChange={(e) => addFiles(e.target.files)}
                  data-testid="qa-file-input"
                />
              </div>

              {files.length > 0 && (
                <ul className="mt-4 space-y-2" data-testid="qa-file-list">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center gap-3 p-3 border border-white/5 rounded-lg">
                      <FileText size={15} className="text-cf-gold-light shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-sm truncate">{f.name}</div>
                        <div className="text-xs text-cf-muted">{(f.size / 1024).toFixed(0)} KB</div>
                      </div>
                      <button onClick={() => removeFile(i)} className="text-cf-muted hover:text-rose-400">
                        <X size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <div className="flex items-center gap-3 mt-5">
                <button
                  data-testid="qa-analyze-btn"
                  onClick={analyze}
                  disabled={files.length === 0 || analyzing}
                  className="cf-btn-ghost"
                  style={{
                    fontSize: "0.75rem",
                    background: files.length === 0 || analyzing ? undefined : "#B68A35",
                    color: files.length === 0 || analyzing ? undefined : "#fff",
                    borderColor: files.length === 0 || analyzing ? undefined : "#B68A35",
                  }}
                >
                  {analyzing ? <><Loader size={13} className="animate-spin" /> Analyzing…</> : <><Sparkles size={13} /> Analyze with AI</>}
                </button>
                {(files.length > 0 || result) && (
                  <button onClick={reset} className="cf-btn-ghost" style={{ fontSize: "0.7rem" }}>Reset</button>
                )}
              </div>
              {analyzing && (
                <p className="text-cf-muted text-xs mt-3">Reading {files.length} document{files.length === 1 ? "" : "s"} — typically 20–60 seconds for full bank statements.</p>
              )}
            </div>
          </div>

          <div className="lg:col-span-5">
            {!result && !analyzing && (
              <div className="border border-dashed border-white/10 rounded-lg p-6 text-center text-cf-muted text-sm">
                <Sparkles size={24} className="mx-auto text-cf-gold-light mb-3" strokeWidth={1.5} />
                Your underwriting memo will appear here.
              </div>
            )}
            {result && (
              <AIAnalysisCard analysis={result} onAnalyze={analyze} analyzing={analyzing} hasDocs={true} />
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default QuickAnalyze;
